import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { OpportunityCard } from "@/components/ui/opportunity-card";
import { cn } from "@/lib/utils";

interface Opportunity {
  id: string;
  opportunity_number: string;
  match_score: number;
  price: number;
  status: "new" | "in_review" | "completed" | "claimed" | "claim_approved" | "claim_rejected";
  declined_at?: string | null;
  request?: {
    category: string;
    urgency: string;
    requester_company: string | null;
    compliance_relevant?: boolean;
    onsite_required?: boolean;
  };
}

interface OpportunityStackProps {
  opportunities: Opportunity[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  emptyMessage: string;
}

const categoryLabels: Record<string, string> = {
  managed_service: "Managed Services",
  project: "IT-Projekt",
  security: "Security & Compliance",
  cloud: "Cloud & Infrastruktur",
  support: "IT-Support",
};

const urgencyLabels: Record<string, string> = {
  immediate: "Sofort",
  within_48h: "48 Stunden",
  within_7_days: "7 Tage",
  planned: "Geplant",
};

const OpportunityStack = ({
  opportunities,
  selectedId,
  onSelect,
  emptyMessage,
}: OpportunityStackProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Derive available categories from opportunities
  const availableCategories = useMemo(() => {
    const cats = new Set<string>();
    opportunities.forEach(o => {
      if (o.request?.category) cats.add(o.request.category);
    });
    return Array.from(cats);
  }, [opportunities]);

  const filtered = useMemo(() => {
    let result = opportunities;

    if (categoryFilter !== "all") {
      result = result.filter(o => o.request?.category === categoryFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(o =>
        o.opportunity_number.toLowerCase().includes(q) ||
        (o.request?.requester_company || "").toLowerCase().includes(q) ||
        (categoryLabels[o.request?.category || ""] || o.request?.category || "").toLowerCase().includes(q)
      );
    }

    return result;
  }, [opportunities, search, categoryFilter]);

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("dashboard.searchPlaceholder", "Suchen (Nr., Unternehmen, Kategorie)...")}
          className="w-full h-10 pl-10 pr-4 border border-border bg-background text-sm focus:border-primary transition-colors"
        />
      </div>

      {/* Category Filter */}
      {availableCategories.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setCategoryFilter("all")}
            className={cn(
              "px-3 py-1.5 text-xs border transition-colors",
              categoryFilter === "all"
                ? "border-accent bg-accent/5 text-foreground"
                : "border-border text-muted-foreground hover:text-foreground"
            )}
          >
            {t("dashboard.filterAll", "Alle")}
          </button>
          {availableCategories.map(cat => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={cn(
                "px-3 py-1.5 text-xs border transition-colors",
                categoryFilter === cat
                  ? "border-accent bg-accent/5 text-foreground"
                  : "border-border text-muted-foreground hover:text-foreground"
              )}
            >
              {categoryLabels[cat] || cat}
            </button>
          ))}
        </div>
      )}

      {/* List */}
      {filtered.length === 0 ? (
        <div className="p-12 border border-dashed border-border text-center">
          <p className="text-caption">
            {opportunities.length === 0 ? emptyMessage : t("dashboard.noSearchResults", "Keine Ergebnisse gefunden")}
          </p>
        </div>
      ) : (
        filtered.map((opp) => (
          <OpportunityCard
            key={opp.id}
            id={opp.id}
            opportunityNumber={opp.opportunity_number}
            category={categoryLabels[opp.request?.category || ""] || opp.request?.category || ""}
            company={opp.status === "new" || opp.declined_at ? "Vertraulich" : (opp.request?.requester_company || "Unternehmen")}
            urgency={urgencyLabels[opp.request?.urgency || ""] || opp.request?.urgency || ""}
            matchScore={opp.match_score}
            price={opp.price}
            status={opp.status}
            isSelected={selectedId === opp.id}
            onClick={() => onSelect(opp.id)}
            complianceFlag={opp.request?.compliance_relevant}
            onsiteFlag={opp.request?.onsite_required}
          />
        ))
      )}
    </div>
  );
};

export default OpportunityStack;
